import React, { useEffect, useState, useCallback } from "react";
import { useAccount } from "../../contexts/AccountContext";
import { useAuth } from "../../contexts/AuthContext";
import {
  TeamService,
  MemberWithProfile,
  RoleWithId,
  InviteWithId,
} from "../../services/TeamService";
import { MemberStatus } from "@visitproof/types";
import { Heading } from "../../design/heading";
import { Button } from "../../design/button";
import { Badge } from "../../design/badge";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
} from "../../design/table";
import { Text } from "../../design/text";
import { InviteMemberDialog } from "./InviteMemberDialog";
import { MemberRow } from "./MemberRow";
import { PlusIcon, TrashIcon, InformationCircleIcon } from "@heroicons/react/24/outline";

const isDebugMode = process.env.REACT_APP_DEBUG_MODE === "true" || process.env.NODE_ENV === "development";

export const TeamPage: React.FC = () => {
  const { account } = useAccount();
  const { user, claims } = useAuth();

  const [members, setMembers] = useState<MemberWithProfile[]>([]);
  const [roles, setRoles] = useState<RoleWithId[]>([]);
  const [invites, setInvites] = useState<InviteWithId[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);

  const accountId = account?.id;

  // Check permissions
  const currentMember = members.find((m) => m.id === user?.uid);
  const canInvite = currentMember?.permissions?.includes("team.invite") ?? false;
  const canManageRoles = currentMember?.permissions?.includes("team.manage_roles") ?? false;

  useEffect(() => {
    if (!accountId) return;

    setLoading(true);
    setError(null);

    const unsubMembers = TeamService.subscribeToMembers(
      accountId,
      (newMembers) => {
        setMembers(newMembers);
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );

    const unsubRoles = TeamService.subscribeToRoles(
      accountId,
      setRoles,
      (err) => setError(err.message)
    );

    const unsubInvites = TeamService.subscribeToInvites(
      accountId,
      setInvites,
      (err) => setError(err.message)
    );

    return () => {
      unsubMembers();
      unsubRoles();
      unsubInvites();
    };
  }, [accountId]);

  const handleInviteSuccess = useCallback(() => {
    setIsInviteDialogOpen(false);
  }, []);

  const handleStatusChange = useCallback(
    async (memberId: string, status: MemberStatus) => {
      if (!accountId) return;
      try {
        await TeamService.setMemberStatus(accountId, memberId, status);
      } catch (err) {
        console.error("Failed to update status:", err);
        setError("Failed to update member status");
        // Rethrow so MemberRow's await sees the failure and can reset its
        // own saving state rather than treating the change as successful.
        throw err;
      }
    },
    [accountId]
  );

  const handleDeleteInvite = useCallback(
    async (inviteId: string) => {
      if (!accountId) return;
      // eslint-disable-next-line no-restricted-globals
      if (!window.confirm("Are you sure you want to delete this invite? The recipient will no longer be able to join using this invitation.")) {
        return;
      }
      try {
        await TeamService.deleteInvite(accountId, inviteId);
      } catch (err) {
        console.error("Failed to delete invite:", err);
        setError("Failed to delete invite");
      }
    },
    [accountId]
  );

  if (!accountId) {
    return (
      <div className="p-6">
        <Text>No account selected.</Text>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6">
        <Text>Loading team members...</Text>
      </div>
    );
  }

  // Filter out pending invites that haven't been accepted
  const pendingInvites = invites.filter((invite) => !invite.acceptedAt);
  const activeMembers = members.filter((m) => m.status === "active");
  const suspendedMembers = members.filter((m) => m.status === "suspended");

  return (
    <div className="p-6 space-y-8">
      <div className="flex items-center justify-between">
        <Heading>Team</Heading>
        {canInvite && (
          <Button color="indigo" onClick={() => setIsInviteDialogOpen(true)}>
            <PlusIcon className="w-5 h-5 mr-1" />
            Invite Member
          </Button>
        )}
      </div>

      {error && (
        <div className="p-4 rounded-md bg-red-50 dark:bg-red-900/20">
          <Text className="text-red-700 dark:text-red-300">{error}</Text>
        </div>
      )}

      {/* Pricing Notice */}
      <div className="flex items-start gap-3 p-4 border border-blue-200 rounded-lg bg-blue-50 dark:bg-blue-900/20 dark:border-blue-800">
        <InformationCircleIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
        <div>
          <Text className="font-medium text-blue-900 dark:text-blue-100">
            Team Pricing
          </Text>
          <Text className="mt-1 text-sm text-blue-700 dark:text-blue-300">
            Admin seats are $25/month each. When you invite a new administrator, a seat is added to your subscription.
            Suspending an admin removes the seat. The account owner is always included at no extra cost.
          </Text>
        </div>
      </div>

      {/* Active Members */}
      <section>
        <Heading level={2} className="mb-4">
          Active Members ({activeMembers.length})
        </Heading>
        {activeMembers.length === 0 ? (
          <Text className="text-zinc-500">No active members.</Text>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableHeader>Name</TableHeader>
                <TableHeader>Type</TableHeader>
                <TableHeader>Roles</TableHeader>
                <TableHeader>Status</TableHeader>
                {canManageRoles && <TableHeader>Actions</TableHeader>}
              </TableRow>
            </TableHead>
            <TableBody>
              {activeMembers.map((member) => (
                <MemberRow
                  key={member.id}
                  member={member}
                  roles={roles}
                  canManageRoles={canManageRoles}
                  isCurrentUser={member.id === user?.uid}
                  accountId={accountId || ""}
                  onStatusChange={handleStatusChange}
                />
              ))}
            </TableBody>
          </Table>
        )}
      </section>

      {/* Pending Invites */}
      {canInvite && pendingInvites.length > 0 && (
        <section>
          <Heading level={2} className="mb-4">
            Pending Invites ({pendingInvites.length})
          </Heading>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeader>Contact</TableHeader>
                <TableHeader>Type</TableHeader>
                <TableHeader>Roles</TableHeader>
                <TableHeader>Invited</TableHeader>
                <TableHeader>Expires</TableHeader>
                <TableHeader>Actions</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {pendingInvites.map((invite) => (
                <TableRow key={invite.id}>
                  <TableCell>
                    <Text className="font-medium">
                      {invite.email || invite.phone}
                    </Text>
                    <Badge color="zinc" className="ml-2">
                      {invite.channel}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge
                      color={
                        invite.intendedMemberType === "staff"
                          ? "blue"
                          : invite.intendedMemberType === "supervisor"
                          ? "green"
                          : "amber"
                      }
                    >
                      {invite.intendedMemberType}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {invite.roleIds.map((roleId) => {
                        const role = roles.find((r) => r.id === roleId);
                        return (
                          <Badge key={roleId} color="zinc">
                            {role?.name || roleId}
                          </Badge>
                        );
                      })}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Text className="text-zinc-500">
                      {invite.createdAt?.toDate?.()?.toLocaleDateString() ?? "N/A"}
                    </Text>
                  </TableCell>
                  <TableCell>
                    <Text className="text-zinc-500">
                      {invite.expiresAt?.toDate?.()?.toLocaleDateString() ?? "N/A"}
                    </Text>
                  </TableCell>
                  <TableCell>
                    <Button
                      outline
                      onClick={() => handleDeleteInvite(invite.id)}
                      className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </section>
      )}

      {/* Suspended Members */}
      {canManageRoles && suspendedMembers.length > 0 && (
        <section>
          <Heading level={2} className="mb-4">
            Suspended Members ({suspendedMembers.length})
          </Heading>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeader>Name</TableHeader>
                <TableHeader>Type</TableHeader>
                <TableHeader>Roles</TableHeader>
                <TableHeader>Status</TableHeader>
                <TableHeader>Actions</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {suspendedMembers.map((member) => (
                <MemberRow
                  key={member.id}
                  member={member}
                  roles={roles}
                  canManageRoles={canManageRoles}
                  isCurrentUser={member.id === user?.uid}
                  accountId={accountId || ""}
                  onStatusChange={handleStatusChange}
                />
              ))}
            </TableBody>
          </Table>
        </section>
      )}

      {/* Debug Panel - only visible in debug/dev mode */}
      {isDebugMode && (
        <section className="p-4 mt-8 border rounded-lg border-amber-300 bg-amber-50 dark:bg-amber-900/20">
          <Heading level={3} className="mb-2 text-amber-700 dark:text-amber-300">
            Debug Info
          </Heading>
          <div className="space-y-2 font-mono text-xs">
            <div>
              <Text className="font-bold text-amber-700 dark:text-amber-300">User ID:</Text>
              <Text className="text-amber-600 dark:text-amber-400">{user?.uid}</Text>
            </div>
            <div>
              <Text className="font-bold text-amber-700 dark:text-amber-300">Account ID:</Text>
              <Text className="text-amber-600 dark:text-amber-400">{accountId}</Text>
            </div>
            <div>
              <Text className="font-bold text-amber-700 dark:text-amber-300">Custom Claims:</Text>
              <pre className="p-2 mt-1 overflow-auto text-xs rounded max-h-48 bg-amber-100 dark:bg-amber-800 text-amber-800 dark:text-amber-200">
                {JSON.stringify(claims, null, 2)}
              </pre>
            </div>
            <div>
              <Text className="font-bold text-amber-700 dark:text-amber-300">Current Member:</Text>
              <pre className="p-2 mt-1 overflow-auto text-xs rounded max-h-48 bg-amber-100 dark:bg-amber-800 text-amber-800 dark:text-amber-200">
                {JSON.stringify(currentMember, null, 2)}
              </pre>
            </div>
            <div>
              <Text className="font-bold text-amber-700 dark:text-amber-300">Permissions:</Text>
              <Text className="text-amber-600 dark:text-amber-400">
                canInvite: {canInvite ? "true" : "false"}, canManageRoles: {canManageRoles ? "true" : "false"}
              </Text>
            </div>
          </div>
        </section>
      )}

      {/* Invite Member Dialog */}
      <InviteMemberDialog
        open={isInviteDialogOpen}
        onClose={() => setIsInviteDialogOpen(false)}
        accountId={accountId}
        onSuccess={handleInviteSuccess}
      />
    </div>
  );
};

export default TeamPage;
